import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { AdminSignIn, AdminNav } from "@/components/admin/AdminShell";
import { getSystemHealth, type HealthReport } from "@/lib/admin-health.functions";

export const Route = createFileRoute("/admin/health")({
  component: AdminHealthPage,
  head: () => ({
    meta: [
      { title: "System health · SFN internal" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const DOT: Record<string, string> = {
  ok: "bg-emerald-400",
  warn: "bg-amber-400",
  fail: "bg-red-400",
};
const TEXT: Record<string, string> = {
  ok: "text-emerald-300",
  warn: "text-amber-300",
  fail: "text-red-300",
};

function AdminHealthPage() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [report, setReport] = useState<HealthReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useServerFn(getSystemHealth);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(Boolean(data.session)));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) =>
      setAuthed(Boolean(session)),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport((await run()) as HealthReport);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Health check failed.");
    } finally {
      setLoading(false);
    }
  }, [run]);

  useEffect(() => {
    if (authed) void refresh();
  }, [authed, refresh]);

  if (authed === null) return null;

  return (
    <main className="mx-auto max-w-4xl px-5 pb-24 pt-16">
      <h1 className="text-2xl font-bold tracking-tight">System health</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Internal status of the contact-forwarding pipeline.
      </p>

      {!authed ? (
        <AdminSignIn note="Sign in with your internal admin account." />
      ) : (
        <>
          <AdminNav current="health" />

          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={loading}
              className="rounded-full bg-white/[0.08] px-4 py-1.5 text-xs font-semibold transition hover:bg-white/[0.14] disabled:opacity-50"
            >
              {loading ? "Checking…" : "Run health check"}
            </button>
            {report && (
              <span className="text-xs text-muted-foreground">
                Last checked {new Date(report.checkedAt).toLocaleString()}
              </span>
            )}
          </div>

          {error && <p className="mt-4 text-sm text-red-300">{error}</p>}

          {report && (
            <>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <Stat
                  label="Last successful dispatch"
                  value={
                    report.lastSuccessfulDispatch
                      ? new Date(report.lastSuccessfulDispatch).toLocaleString()
                      : "Never"
                  }
                />
                <Stat label="Awaiting dispatch" value={String(report.pendingCount)} />
                <Stat label="Failed dispatches" value={String(report.failedCount)} />
              </div>

              <ul className="mt-6 space-y-2">
                {report.checks.map((c) => (
                  <li
                    key={c.key}
                    className="flex items-start gap-3 rounded-2xl glass px-4 py-3"
                  >
                    <span
                      className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${DOT[c.state]}`}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">
                        {c.label}{" "}
                        <span className={`text-[11px] uppercase tracking-wider ${TEXT[c.state]}`}>
                          {c.state}
                        </span>
                      </p>
                      <p className="mt-0.5 break-words text-xs text-muted-foreground">
                        {c.detail}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl glass p-4">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold">{value}</p>
    </div>
  );
}
